import http from '@frontend/api/http';
import { HYPATIA_URL, OPAL_BACKEND_URL } from '@frontend/envConfig';
import {
  AdminReviewRequest,
  AgentExecutionResponse,
  AgentFileUrl,
  AgentVersionListResponse,
  ConversationEvalSampleCreate,
  ConversationEvalSampleFromConversation,
  ConversationEvaluationSample,
  EvaluationSample,
  EvaluationSampleCreate,
  EvaluationSamplesResponse,
  FeedbackListResponse,
  FeedbackResponse,
  MemoryChunkList,
  SpecializedAgent,
  SpecializedAgentExecution,
  SpecializedAgentListItem,
  UserFeedbackRequest,
} from '@frontend/types';

import { AgentIdStatus } from '../components/types/agent-id';
import { triggerBlobDownload } from '../utils/downloadFileResponse';

import type { ImportValidationReport } from './import-validation-types';
import { ensureUtf8 } from './utf8-converter';

export function getSpecializedAgents({
  instanceId,
  includeInactive = true,
  page = 1,
  pageSize = 1000,
  includeDeleted = false,
}: {
  instanceId: string;
  page?: number;
  pageSize?: number;
  includeDeleted?: boolean;
  includeInactive?: boolean;
}) {
  return async () => {
    const res = await http.get<{ items: SpecializedAgentListItem[] }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized`,
      {
        params: {
          page,
          page_size: pageSize,
          include_deleted: includeDeleted,
          include_inactive: includeInactive,
        },
      }
    );
    return res.data.items;
  };
}

getSpecializedAgents.key = (instanceId: string, page = 1, pageSize = 20, includeDeleted = false) =>
  ['opal:specialized-agents', instanceId, page, pageSize, includeDeleted] as const;

export const getSpecializedAgentKey = (agentId: string) =>
  ['opal:specialized-agent', agentId] as const;

export function getSpecializedAgent(url: string) {
  return async () => {
    const res = await http.get<SpecializedAgent>(url);
    return res.data;
  };
}

export function createSpecializedAgent(agent: SpecializedAgent, instanceId: string) {
  return async () => {
    return http.post<SpecializedAgent>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized`,
      agent
    );
  };
}

export async function uploadFile(
  file: File,
  customerId: string,
  instanceId: string
): Promise<AgentFileUrl> {
  // Ensure text files are UTF-8 encoded before uploading
  const processedFile = await ensureUtf8(file);

  // Step 1: Request signed URL
  const signedUrlResponse = await http.post<{
    signed_url: string;
    file_id: string;
    expires_at: number;
    content_type: string;
  }>(
    `${OPAL_BACKEND_URL}/api/v2/customer/${customerId}/files/upload/signed-url?instance_id=${instanceId}`,
    {
      filename: processedFile.name,
      mime_type: processedFile.type,
      size: processedFile.size,
    }
  );

  const { signed_url, file_id } = signedUrlResponse.data;

  // Step 2: Upload directly to GCS
  await fetch(signed_url, {
    method: 'PUT',
    body: processedFile,
    headers: {
      'Content-Type': processedFile.type,
    },
  });

  // Step 3: Create file metadata
  const metadataResponse = await http.post<{
    file_id: string;
    name: string;
    mime_type: string;
    size: number;
    uri: string;
    customer_id: string;
    link: string;
  }>(
    `${OPAL_BACKEND_URL}/api/v2/customer/${customerId}/files/${file_id}/create?instance_id=${instanceId}`,
    {
      filename: processedFile.name,
      mime_type: processedFile.type,
      size: processedFile.size,
    }
  );

  const data = metadataResponse.data;
  return {
    url: data.uri,
    type: data.mime_type,
    description: data.name || null,
    fileId: data.file_id,
    originalName: processedFile.name,
    size: data.size,
  } as AgentFileUrl;
}

export function updateSpecializedAgent(url: string, agent: SpecializedAgent) {
  return async () => {
    return http.patch(url, agent);
  };
}

// TODO: combine updateSpecializedAgent and updateAgentBaselineScore into a single update function with more flexible payload
export function updateAgentBaselineScore(
  instanceId: string,
  agentId: string,
  baselineScore: number
) {
  return async () => {
    const url = `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}`;
    return http.patch(url, { baseline_evaluation_score: baselineScore });
  };
}

updateAgentBaselineScore.key = (instanceId: string, agentId: string) => [
  'update-baseline-score',
  instanceId,
  agentId,
];

export function deleteSpecializedAgent(deleteUrl: string) {
  return async () => {
    return http.delete(deleteUrl);
  };
}

export function checkAgentIdAvailability(agentId: string, instanceId: string) {
  return async () => {
    const res = await http.post<AgentIdStatus>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/check-agent-id`,
      {
        agent_id: agentId,
      }
    );
    return res.data;
  };
}

export function executeSpecializedAgent(
  executeUrl: string,
  parameters: Record<string, unknown>,
  runInBackground = true
) {
  return async () => {
    const res = await http.post<AgentExecutionResponse>(
      executeUrl,
      { parameters },
      {
        params: {
          run_in_background: runInBackground,
        },
        timeout: 30 * 60 * 10000, // 10 minutes timeout for agent execution
      }
    );
    return { data: res.data, etag: res.data.etag ?? (res.headers?.['etag'] as string | undefined) };
  };
}

export function getSpecializedAgentExecution(instanceId: string, executionId: string) {
  return async () => {
    const res = await http.get<SpecializedAgentExecution>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/executions/${executionId}`
    );
    return res.data;
  };
}

export function getAgentMemoryChunks(memoryUrl: string) {
  return async () => {
    const res = await http.get<MemoryChunkList>(`${memoryUrl}/chunks`);
    return res.data;
  };
}

export const exportSpecializedAgent = async (instanceId: string, agentId: string) => {
  const res = await http.get<{ data: unknown }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/export`
  );
  return res.data.data;
};

export const validateSpecializedAgentImport = async (instanceId: string, agentData: object) => {
  const res = await http.post<{ data: ImportValidationReport }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/import/validate`,
    agentData
  );
  return res.data.data;
};

export const importSpecializedAgent = async (
  instanceId: string,
  agentData: object,
  mode: 'skip' | 'overwrite' = 'overwrite',
  conflictResolutions?: Record<string, string>
) => {
  if (conflictResolutions && Object.keys(conflictResolutions).length > 0) {
    const res = await http.post(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/import?mode=${mode}`,
      { agent_data: agentData, conflict_resolutions: conflictResolutions }
    );
    return res.data;
  }
  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/import?mode=${mode}`,
    agentData
  );
  return res.data;
};

// Evaluation Samples API
export function getEvaluationSamples(instanceId: string, agentId: string) {
  return async () => {
    const res = await http.get<EvaluationSamplesResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples`
    );
    return res.data.data.samples;
  };
}

getEvaluationSamples.key = (instanceId: string, agentId: string) =>
  ['opal:evaluation-samples', instanceId, agentId] as const;

export function createEvaluationSample(
  instanceId: string,
  agentId: string,
  data: EvaluationSampleCreate
) {
  return async () => {
    const res = await http.post<{ data: EvaluationSample }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples`,
      data
    );
    return res.data.data;
  };
}

export function updateEvaluationSample(
  instanceId: string,
  agentId: string,
  sampleId: string,
  data: EvaluationSampleCreate
) {
  return async () => {
    const res = await http.patch<{ data: EvaluationSample }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples/${sampleId}`,
      data
    );
    return res.data.data;
  };
}

export function deleteEvaluationSample(instanceId: string, agentId: string, sampleId: string) {
  return async () => {
    await http.delete(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples/${sampleId}`
    );
  };
}

export function stopSpecializedAgentExecution(
  instanceId: string,
  executionId: string,
  errorMessage: string
) {
  return async () => {
    const res = await http.patch<SpecializedAgentExecution>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/executions/${executionId}`,
      {
        error_message: errorMessage,
        status: 'FAILED',
      }
    );
    return res.data;
  };
}

export function resumeSpecializedAgentExecution(instanceId: string, executionId: string) {
  return async () => {
    const res = await http.post<SpecializedAgentExecution>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/executions/${executionId}/resume`
    );
    return res.data;
  };
}

export type StopWaitingResponse = {
  cancelled_tool_call_ids: string[];
  resumed: boolean;
};

export function stopWaitingSpecializedAgentExecution(instanceId: string, executionId: string) {
  return async () => {
    const res = await http.post<StopWaitingResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/executions/${executionId}/stop-waiting`
    );
    return res.data;
  };
}

// Export execution logs as CSV or Excel file download.
// When a date range is set, `limit` is omitted (the backend rejects limit+range)
// and the timeout is raised to accommodate the batched/ZIP path.
export async function exportExecutionLogs(
  instanceId: string,
  agentId: string,
  fields: string[],
  limit = 500,
  format: 'csv' | 'xlsx' = 'csv',
  startDate?: string,
  endDate?: string
): Promise<void> {
  const hasRange = Boolean(startDate || endDate);
  const body: Record<string, unknown> = { fields, format };
  if (hasRange) {
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
  } else {
    body.limit = limit;
  }

  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/executions/export`,
    body,
    {
      responseType: 'blob',
      timeout: hasRange ? 10 * 60 * 1000 : 5 * 60 * 1000,
    }
  );

  triggerBlobDownload(
    res.data as Blob,
    res.headers['content-disposition'] as string | undefined,
    `${agentId}-executions.${format}`
  );
}

// Stream execute URL construction
export function getStreamExecuteUrl(executeUrl: string): string {
  return executeUrl.replace(/\/execute$/, '/stream-execute');
}

// Agent Version History API
export function getSpecializedAgentVersions(
  instanceId: string,
  agentId: string,
  limit = 20,
  offset = 0
) {
  return async () => {
    const res = await http.get<AgentVersionListResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/versions`,
      { params: { limit, offset } }
    );
    return res.data;
  };
}

getSpecializedAgentVersions.key = (instanceId: string, agentId: string, limit = 20, offset = 0) =>
  ['opal:specialized-agent-versions', instanceId, agentId, limit, offset] as const;

export function getSpecializedAgentVersion(versionUrl: string) {
  return async () => {
    const res = await http.get<SpecializedAgent>(versionUrl);
    return res.data;
  };
}

// Conversation Feedback API
export function submitUserFeedback(
  instanceId: string,
  memoryId: string,
  turn: number,
  data: UserFeedbackRequest
) {
  return async () => {
    const res = await http.post<FeedbackResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/chat/${memoryId}/turns/${turn}/feedback`,
      data
    );
    return res.data;
  };
}

export function getFeedback(instanceId: string, memoryId: string) {
  return async () => {
    const res = await http.get<FeedbackListResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/chat/${memoryId}/feedback`
    );
    return res.data;
  };
}

getFeedback.key = (instanceId: string, memoryId: string) =>
  ['opal:conversation-feedback', instanceId, memoryId] as const;

export function submitAdminReview(instanceId: string, memoryId: string, data: AdminReviewRequest) {
  return async () => {
    const res = await http.post<FeedbackResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/chat/${memoryId}/feedback`,
      data
    );
    return res.data;
  };
}

getSpecializedAgentVersion.key = (versionUrl: string) =>
  ['opal:specialized-agent-version', versionUrl] as const;

// Conversation Evaluation Samples API
const convEvalBase = (instanceId: string, agentId: string) =>
  `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/conversation-evaluation-samples`;

export function getConversationEvaluationSamples(instanceId: string, agentId: string) {
  return async () => {
    const res = await http.get<{
      data: { samples: ConversationEvaluationSample[]; total: number };
    }>(convEvalBase(instanceId, agentId));
    return res.data.data;
  };
}

getConversationEvaluationSamples.key = (instanceId: string, agentId: string) =>
  ['opal:conversation-eval-samples', instanceId, agentId] as const;

export function createConversationEvaluationSample(
  instanceId: string,
  agentId: string,
  data: ConversationEvalSampleCreate
) {
  return async () => {
    const res = await http.post<{ data: ConversationEvaluationSample }>(
      convEvalBase(instanceId, agentId),
      data
    );
    return res.data.data;
  };
}

export function createConversationSampleFromConversation(
  instanceId: string,
  agentId: string,
  data: ConversationEvalSampleFromConversation
) {
  return async () => {
    const res = await http.post<{ data: ConversationEvaluationSample }>(
      `${convEvalBase(instanceId, agentId)}/from-conversation`,
      data
    );
    return res.data.data;
  };
}

export function updateConversationEvaluationSample(
  instanceId: string,
  agentId: string,
  sampleId: string,
  data: Partial<ConversationEvalSampleCreate>
) {
  return async () => {
    const res = await http.patch<{ data: ConversationEvaluationSample }>(
      `${convEvalBase(instanceId, agentId)}/${sampleId}`,
      data
    );
    return res.data.data;
  };
}

export function deleteConversationEvaluationSample(
  instanceId: string,
  agentId: string,
  sampleId: string
) {
  return async () => {
    await http.delete(`${convEvalBase(instanceId, agentId)}/${sampleId}`);
  };
}

// Memory Chunks API (for building conversation eval samples from execution memory)
export function getConversationMemoryChunks(instanceId: string, memoryId: string) {
  return async () => {
    const res = await http.get<MemoryChunkList>(
      `${HYPATIA_URL}/api/v1/${instanceId}/memories/${memoryId}/chunks`
    );
    return res.data;
  };
}

// Evaluation Criteria API
export function updateEvaluationCriteria(instanceId: string, agentId: string, criteria: string[]) {
  return async () => {
    const url = `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}`;
    return http.patch(url, { evaluation_criteria: criteria });
  };
}
